import React, { useRef } from 'react';
import { PersistGate } from 'redux-persist/integration/react';
import { useStandaloneAcl } from '../../../Services/acl.service';
import { useEffectAsync } from '../../../helpers';
import Loading, { LoadingSize } from '../../../Components/Loading';
import { useDispatch, useSelector } from 'react-redux';
import {
	resetAcl,
	selectAclIsLoaded,
	selectAclIsLoading,
	selectAclLoadedAt,
	selectAclTree,
	setAclLoading,
	setAclTree,
} from '../../../store/reducers/aclSlice';
import useWorkplaceRole from '../../Workplace/Hooks/useWorkplaceRole';
import { useAppContext } from '../../../Services/AppContext';
import { persistor } from '../../../store/store';
import useSocket from '../../Base/Hooks/useSocket';
import { Socket } from '../../Base/Enums/Socket.enum';

const ACL_TTL_MS = 30 * 60 * 1000;

interface AclContextProps {
	isAllowed: (resource: string, permission?: string) => boolean;
}

const AclContext = React.createContext<AclContextProps>({
	isAllowed: () => false,
});

interface AclContextProviderProps {
	children: React.ReactElement | React.ReactElement[];
	loadingSize?: LoadingSize;
	showLoading?: boolean;
}

const AclContextProviderInner: React.FC<AclContextProviderProps> = props => {
	const { loggedUserId, workplaceId } = useAppContext();
	const { children, loadingSize, showLoading = true } = props;

	const { loadAcl, isAllowed: standaloneIsAllowed } = useStandaloneAcl();

	const aclIsLoading = useSelector(selectAclIsLoading);
	const aclIsLoaded = useSelector(selectAclIsLoaded);
	const aclLoadedAt = useSelector(selectAclLoadedAt);
	const aclData = useSelector(selectAclTree);

	const dispatch = useDispatch();

	const aclReloadingRef = useRef(false);

	useWorkplaceRole({
		userId: loggedUserId,
		workplaceId,
	});

	const reloadAcl = async () => {
		if (aclReloadingRef.current) {
			return;
		}

		aclReloadingRef.current = true;
		dispatch(setAclLoading(true));

		try {
			const tmpAclData = await loadAcl();
			dispatch(setAclTree(tmpAclData));
		} finally {
			dispatch(setAclLoading(false));
			aclReloadingRef.current = false;
		}
	};

	useSocket(Socket.USER_ACL_REFRESH, async () => {
		await reloadAcl();
	});

	const aclIsExpired = aclIsLoaded
		? !aclLoadedAt || Date.now() - aclLoadedAt > ACL_TTL_MS
		: false;

	useEffectAsync(async () => {
		if (aclIsLoading) {
			return;
		}

		if (aclIsLoaded && !aclIsExpired) {
			return;
		}

		if (aclIsExpired) {
			dispatch(resetAcl());
		}

		await reloadAcl();
	}, [aclIsLoaded, aclIsLoading, aclLoadedAt]);

	const isAllowed = (resource: string, permission?: string) =>
		standaloneIsAllowed(aclData, resource, permission);

	return (
		<AclContext.Provider value={{ isAllowed }}>
			{aclIsLoading && showLoading ? (
				<div className={'py-5'}>
					<Loading size={loadingSize} />
				</div>
			) : (
				children
			)}
		</AclContext.Provider>
	);
};

const AclContextProvider: React.FC<AclContextProviderProps> = props => {
	return (
		<PersistGate loading={null} persistor={persistor}>
			<AclContextProviderInner {...props} />
		</PersistGate>
	);
};

export const useAclContext = () => React.useContext(AclContext);

export default AclContextProvider;
